<script setup lang="ts">
import { computed } from 'vue';
import { upperFirst } from 'lodash-es';
import type {
  KbdItem,
  ShortcutData,
} from '@/common/components/molecules/keyboardShortcutGuide/keyboardShortcutGuide.type';
import { IconType } from '@/types/icon.types';

interface Props {
  data: ShortcutData;
}
const props = defineProps<Props>();

const KBD_ITEMS: KbdItem[] = [
  'left',
  'right',
  'up',
  'down',
  'enter',
  'tab',
  'esc',
  'control',
  'shift',
  'alt',
  'meta',
];
const checkKbdStyle = (kbdItem: KbdItem | string): kbdItem is KbdItem => {
  return KBD_ITEMS.includes(kbdItem as KbdItem);
};

const getKbdIcon = (kbdItem: KbdItem): IconType | undefined => {
  switch (kbdItem) {
    case 'left':
    case 'right':
    case 'up':
    case 'down':
      return 'ev-icon-allow2-left2';
    case 'enter':
      return 'icon-enter';
    default:
      return undefined;
  }
};

const getKbdIconClass = (kbdItem: KbdItem): string | undefined => {
  return `keyboard-shortcut-item__kbd--${kbdItem}`;
};

const kbdItems = computed<
  {
    isDisplayKbd: boolean;
    icon?: IconType;
    iconClass?: string;
    text: string;
  }[]
>(() => {
  const raws = props.data.items;
  return raws.map((r: KbdItem | string) => {
    const isDisplayKbd = checkKbdStyle(r);

    return {
      icon: isDisplayKbd ? getKbdIcon(r) : undefined,
      iconClass: isDisplayKbd ? getKbdIconClass(r) : undefined,
      isDisplayKbd,
      text: r,
    };
  });
});
</script>

<template>
  <h-stack
    class="keyboard-shortcut-item"
    :spacing="4"
    align="center"
  >
    <template
      v-for="(kbd, idx) in kbdItems"
      :key="idx"
    >
      <kbd
        v-if="kbd.isDisplayKbd"
        class="keyboard-shortcut-item__kbd"
        :class="kbd.iconClass"
      >
        <ev-icon
          v-if="kbd.icon"
          :icon="kbd.icon"
          size="xsmall"
        />
        <span v-else>
          {{ upperFirst(kbd.text) }}
        </span>
      </kbd>
      <span v-else>
        {{ kbd.text }}
      </span>
    </template>

    <p>
      {{ props.data.desc }}
    </p>
  </h-stack>
</template>

<style scoped lang="scss">
.keyboard-shortcut-item {
  white-space: nowrap;

  p {
    margin: 0;
    font-size: 11px;
    font-weight: 500;
    line-height: 18px;
    color: var(--color-gray-03-09);
  }
}
.keyboard-shortcut-item__kbd {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 3.5px;
  margin: 0;
  border: 1px solid var(--color-gray-05-05);
  border-radius: 2px;
  background-color: var(--color-gray-07-00);
  font-size: 11px;
  font-weight: 500;
  line-height: 18px;
  color: var(--color-gray-03-09);

  [class^='ev-icon-'],
  [class^='icon-'] {
    line-height: 1;
    color: var(--color-gray-03-09) !important;

    &::before {
      display: inline-block;
      line-height: 1;
    }
  }

  &--left,
  &--up,
  &--down,
  &--right {
    width: 18px;
    padding: 0;
  }

  &--up {
    transform: rotate(90deg);
  }

  &--down {
    transform: rotate(270deg);
  }

  &--right {
    transform: rotate(180deg);
  }

  // left는 아이콘이 이미 왼쪽 방향이므로 transform 불필요

  &--enter :deep(i) {
    mask-size: 11px 11px;
  }
}
</style>
